import { Link } from "@tanstack/react-router";
import { Section, Eyebrow, CTAButton } from "@/components/site/Layout";

import { pricingTiers, startingPrice, type Tier } from "@/data/pricing";
export { pricingTiers, type Tier } from "@/data/pricing";

export function PricingGrid({ tiers = pricingTiers, ctaTo = "/contact" }: { tiers?: Tier[]; ctaTo?: string } = {}) {
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {tiers.map((t) => (
        <div
          key={t.name}
          className={`rounded-xl p-5 sm:p-7 flex flex-col ${
            t.highlight
              ? "bg-[var(--navy-deep)] text-primary-foreground shadow-[var(--shadow-elegant)]"
              : "bg-card border border-border text-foreground"
          }`}
        >
          {t.highlight && (
            <div className="inline-block self-start rounded-full bg-[var(--gold)] text-[var(--navy-deep)] px-3 py-1 text-xs font-bold mb-4">
              MOST POPULAR
            </div>
          )}
          <h3 className="font-display text-2xl font-semibold">{t.name}</h3>
          <div className={`text-sm mt-1 ${t.highlight ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
            {t.best}
          </div>
          <div className="mt-5 font-display">
            <span className="text-4xl font-semibold">{t.price}</span>
            <span className={t.highlight ? "text-primary-foreground/70" : "text-muted-foreground"}>/month</span>
          </div>
          <ul className="mt-6 space-y-2.5 flex-1">
            {t.features.map((f) => (
              <li key={f} className="flex gap-2.5 text-sm leading-relaxed">
                <span className={t.highlight ? "text-[var(--gold)]" : "text-[var(--gold-deep)]"}>✓</span>
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <div className="mt-6">
            <CTAButton to={ctaTo} variant={t.highlight ? "gold" : "primary"}>
              Get started
            </CTAButton>
          </div>
        </div>
      ))}
    </div>
  );
}

export function PricingTeaser({ title, sub, extraLine, linkText }: { title?: string; sub?: string; extraLine?: string; linkText?: string }) {
  return (
    <div className="bg-secondary/40 border-y border-border">
      <Section>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-2xl">
            <Eyebrow>Pricing</Eyebrow>
            <h2 className="mt-4 text-4xl font-semibold text-foreground">
              {title ?? `Plans that start at ${startingPrice}/month.`}
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              {sub ??
                "Six transparent packages, from solo attorneys just entering local search to enterprise firms managing multi-market campaigns. Every plan includes reporting on rankings, calls and signed cases, dedicated specialists, and monthly reporting."}
            </p>
            {extraLine && <p className="mt-4 text-lg text-muted-foreground">{extraLine}</p>}
          </div>
          <Link
            to="/pricing"
            className="text-sm font-semibold text-primary hover:text-[var(--gold-deep)] whitespace-nowrap"
          >
            {linkText ?? "See all 6 plans →"}
          </Link>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {pricingTiers.map((t) => (
            <Link
              key={t.name}
              to="/pricing"
              className={`group rounded-xl p-5 border transition ${
                t.highlight
                  ? "bg-[var(--navy-deep)] text-primary-foreground border-[var(--gold)]"
                  : "bg-background border-border hover:border-[var(--gold)]"
              }`}
            >
              <div className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-3">
                <div className={`font-semibold ${t.highlight ? "text-primary-foreground" : "text-foreground"}`}>
                  {t.name}
                </div>
                <div className={`font-display text-xl font-semibold ${t.highlight ? "text-[var(--gold)]" : "text-foreground"}`}>
                  {t.price}
                </div>
              </div>
              <div className={`mt-2 text-xs ${t.highlight ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                {t.best}
              </div>
            </Link>
          ))}
        </div>
      </Section>
    </div>
  );
}